import AWS from 'aws-sdk'
import momenttz from 'moment-timezone'
import { AWS_SETTINGS } from './constants'
import { formatDate, getCurrentTime } from './rules'

export function getBase64(img) {
  return new Promise((res, rej) => {
    const reader = new FileReader()
    reader.addEventListener('load', () => {
      res({
        data: reader.result,
        name: img.name,
        type: img.type,
      })
    })
    reader.readAsDataURL(img)
  })
}

export async function uploadFileToS3(file, fileName, { folder = 'image/' }) {
  const BUCKET = AWS_SETTINGS.BUCKET_NAME
  const REGION = AWS_SETTINGS.REGION_NAME
  const ACCESS_KEY = AWS_SETTINGS.ACCESS_KEY
  const SECRET_KEY = AWS_SETTINGS.SECRET_KEY

  AWS.config.update({
    accessKeyId: ACCESS_KEY,
    secretAccessKey: SECRET_KEY,
    region: REGION,
  })

  const s3 = new AWS.S3()

  try {
    const result = await s3
      .upload({
        Bucket: BUCKET,
        Key: folder + (fileName || file.name),
        Body: file,
        ACL: 'public-read',
      })
      .promise()
    return result
  } catch (e) {
    return false
  }
}

export const convertBase64ToFile = function (image) {
  let splitImage = image.split(',')
  if (splitImage.length > 1) {
    const byteString = atob(splitImage[1])
    const ab = new ArrayBuffer(byteString.length)
    const ia = new Uint8Array(ab)
    for (let i = 0; i < byteString.length; i += 1) {
      ia[i] = byteString.charCodeAt(i)
    }
    const newBlob = new Blob([ab], {
      //type: 'application/pdf',
    })
    return { blob: newBlob, status: true }
  } else {
    return { blob: '', status: false }
  }
}

export const getCurrentLocation = async () => {
  let location = null

  if (navigator.geolocation) {
    location = await new Promise((res, rej) => {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          res({
            position,
            success: true,
            error: null,
          })
        },
        (error) => {
          rej({
            position: null,
            success: false,
            error,
          })
        }
      )
    })
  } else {
    location = {
      position: null,
      success: false,
      error: null,
    }
  }

  return location
}

export const isCharOrNot = (char = '') => {
  if (char === ' ' || char === '.') return true
  return false
}

export const splitContentByWord = (string = '', limit = 60) => {
  string = string || ''
  if (string.length > limit) {
    let newString = string.substr(0, limit)

    let last = newString.length - 1
    let index = last
    for (index = last; index > -1; index--) {
      let char = newString.charAt(index)

      if (isCharOrNot(char)) {
        break
      }
    }

    return newString.substr(0, index) + '...'
  } else {
    return string
  }
}

export const handleDebounce = (operation) => {
  let currentRequestTimeout = null

  return async (value) => {
    clearTimeout(currentRequestTimeout)
    const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms))
    await delay(500)
    await operation(value)
  }
}

export const handleDebounceMultiple = (operation, timeout = 500) => {
  let currentRequestTimeout = null

  return async (...values) => {
    clearTimeout(currentRequestTimeout)
    const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms))
    await delay(timeout)
    await operation(...values)
  }
}

export const handleDebounceWithPostCall = (
  operation,
  { postCall = () => {} } = {}
) => {
  let currentRequestTimeout = null

  return async (value) => {
    clearTimeout(currentRequestTimeout)
    const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms))
    const result = await operation(value)
    await delay(1600)
    await postCall(result)
  }
}

export const handleDebounceWithPostCallWithTime = (
  operation,
  { time = 500 } = {}
) => {
  let currentRequestTimeout = null

  return async (value, postCall = () => {}) => {
    clearTimeout(currentRequestTimeout)
    const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms))
    const result = await operation(value)
    await delay(time)
    await postCall(result)
  }
}

export const generateRange = (value, init = 0) => {
  let li = []
  for (let i = init; i < init + value; i++) li.push(i)
  return li
}

function isMobileOrTablet() {
  return /(android|iphone|ipad|mobile)/i.test(navigator.userAgent)
}

export function whatsappLink(url, { title, separator = '&url=' }) {
  return (
    'https://' +
    (isMobileOrTablet() ? 'api' : 'web') +
    '.whatsapp.com/send' +
    objectToGetParams({
      text: title ? title + separator + url : url,
    })
  )
}

export function facebookLink(url, { quote, hashtag }) {
  let link =
    'https://www.facebook.com/sharer/sharer.php' +
    objectToGetParams({
      u: url,
      quote,
      hashtag,
    })

  return link
}

export function pinterestLink(url, { media, description }) {
  return (
    'https://pinterest.com/pin/create/button/' +
    objectToGetParams({
      url,
      media,
      description,
    })
  )
}

export function twitterLink(url, { title, via, hashtags = [] }) {
  return (
    'https://twitter.com/share' +
    objectToGetParams({
      url,
      text: title,
      via,
      hashtags: hashtags.join(','),
    })
  )
}

function objectToGetParams(object) {
  return (
    '?' +
    Object.keys(object)
      .filter((key) => !!object[key])
      .map((key) => `${key}=${encodeURIComponent(object[key])}`)
      .join('&')
  )
}

export const getTimeZoneAccordingToUser = (time) => {
  const zone_name = momenttz.tz.guess()
  const timezone = momenttz.tz(time, zone_name)
  return timezone.format('MMM DD YYYY')
}

export const downloadPdf = ({ url = '', headers = {}, method = 'POST' }) => {
  return fetch(url, {
    method,
    headers: {
      ...headers,
      'Content-Type': 'application/x-www-form-urlencoded',
    },
  }).then(async (resp) => {
    return resp.blob()
  })
}

export const getTimeZoneName = async () => {
  return momenttz.tz.names()
}

export const downloadXlxsFile = async ({
  url = '',
  headers = {},
  method = 'POST',
  mode = 'cors',
  body = {}, // if mode is cors add it in the fetch
}) => {
  return fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
      'Access-Control-Allow-Origin': '*',
      ...headers,
    },
    body: serialize(body),
  }).then(async (resp) => {
    return resp.blob()
  })
}

export const generateFormData = (values = {}) => {
  let newForm = new window.FormData()
  for (let key of Object.keys(values)) {
    if (![undefined, null].includes(values[key])) {
      if (Array.isArray(values[key])) {
        if (values[key].length === 1 && key === 'assignUsers') {
          newForm.append(`${key}[0]`, values[key][0])
        } else {
          for (let item of values[key]) {
            newForm.append(key, item)
          }
        }
      } else {
        newForm.append(key, values[key])
      }
    }
  }
  return newForm
}

const serialize = (obj) => {
  const str = []
  for (const p in obj) {
    if (obj.hasOwnProperty(p)) {
      if (typeof obj[p] === 'boolean' || obj[p] === 0 || obj[p]) {
        str.push(encodeURIComponent(p) + '=' + encodeURIComponent(obj[p]))
      }
    }
  }
  return str.join('&')
}

export const calculatePercentage = (source, target, refernceTo = 100) => {
  if (target) {
    return Math.round((source * refernceTo) / target)
  }
  return 0
}

export const calculatePercentageToFixed = (
  source,
  target,
  refernceTo = 100
) => {
  if (target) {
    return ((source * refernceTo) / target).toFixed(2)
  }
  return 0
}

export const queryStringParse = (search) => {
  if (!search.length) {
    return {}
  } else {
    if (search[0] === '?' && search.length > 1) {
      search = search.substr(1)
      let newObj = {}
      let spiltData = search.split('&')
      if (spiltData.length) {
        for (let item of spiltData) {
          let arr = item.split('=')
          if (arr.length) {
            if (arr.length === 1) {
              newObj[arr[0]] = true
            } else if (arr.length === 2) {
              newObj[arr[0]] = arr[1]
            }
          }
        }
        return newObj
      } else {
        return {}
      }
    } else {
      return {}
    }
  }
}

export const downloadFile = (base64Data, name, type, others) => {
  const linkSource = `data:application/pdf;base64,${base64Data}`
  const downloadLink = document.createElement('a')
  const fileExtension = type || '.pdf'
  const date = formatDate(others?.date) || new Date().toString()
  name = name || ''
  const fileName = `${name ? name + '_' : ''}${date}${fileExtension}`

  downloadLink.href = linkSource
  downloadLink.download = fileName
  downloadLink.click()
}

export const downloadFilewithAttachment = (base64Data, name, type, others) => {
  const linkSource = `data:application/pdf;base64,${base64Data.buffer}`
  const zipLinkSource = base64Data.zippedFileUrl
  const downloadLink = document.createElement('a')
  const downloadLinkforZip = document.createElement('a')
  const fileExtension = type || '.pdf'
  const date = formatDate(others?.date) || new Date().toString()
  name = name || ''
  const fileName = `${name ? name + '_' : ''}${date}${fileExtension}`
  downloadLink.href = linkSource
  downloadLink.download = fileName
  if (!(zipLinkSource === null)) {
    downloadLinkforZip.href = zipLinkSource
    downloadLinkforZip.download = fileName
    downloadLinkforZip.click()
  }
  downloadLink.click()
}

export const downloadURI = (uri, name = getCurrentTime()) => {
  let link = document.createElement('a')
  link.download = name
  link.href = uri
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

export const zeroWrapper = (number) => {
  if (number === undefined) {
    return undefined
  }
  if (number) {
    return number
  } else {
    let edgeNumbers = '' + number
    if (edgeNumbers === '0') {
      return '0'
    } else {
      return NaN
    }
  }
}

export const existValueMatchWithObject = (pathName, valueOfObject) => {
  let newPath = undefined
  valueOfObject.filter((route) => {
    if (route[0] === pathName) {
      return (newPath = pathName)
    }
  })
  return newPath
}

export const checkBoolean = (value) => {
  if (value === 'true' || value === true) {
    return true
  }
  return false
}

export const checkBooleanWithValue = (value) => {
  if (value === 'true' || value === 'TRUE' || value === true) {
    return true
  } else if (value === 'false' || value === 'FALSE' || value === false) {
    return false
  }
  return value
}

export const removeHtmlFromString = (value) =>
  value.replace(/<\/?("[^"]*"|'[^']*'|[^>])*(>|$)/g, '')

export const reStructurePermissions = (plans) => {
  let temp = {}
  ;(plans?.features || []).forEach((plan) => {
    let t = {
      NAME: plan?.name || '',
      ORDERING: plan?.ordering || '',
    }
    let name = plan?.name || ''
    name = name?.split(' ') || []
    name = name.join('_') || ''
    temp[name.toUpperCase()] = t
  })
  return temp
}

export const thresholdAlertText = (organization, feature, isSuperAdmin) => {
  if (isSuperAdmin) {
    return `The ${organization} has used all of the allowed number of ${feature}. Upgrade the plan to make room for growth!`
  }
  return `Your organization has reached the ${feature} limit. Please contact the administrator for further help by clicking "OK".`
}
